// Telas de conta: entrar, criar conta, recuperar senha, nova senha, confirmar e-mail.
import { api } from "../api";
import { t } from "../i18n";
import { navigate, type View } from "../router";
import { setUser } from "../session";
import { bindForm, field, screen } from "../ui";

const USERNAME_RE = /^[a-z0-9_]{3,20}$/;

export const loginView: View = (root, { query }) => {
  root.innerHTML = screen(t("auth.login"), "/titulo", `
    <form class="bw-panel narrow bw-stack" novalidate>
      <div class="bw-panel__header">${t("auth.login")}</div>
      <div data-alert hidden></div>
      ${field("login", t("auth.loginField"), { autocomplete: "username" })}
      ${field("password", t("auth.password"), { type: "password", autocomplete: "current-password" })}
      <a class="bw-link" href="#/recuperar-senha">${t("auth.forgotLink")}</a>
      <button class="bw-btn bw-btn--blue bw-btn--block" type="submit">${t("auth.login")}</button>
      <p class="bw-dim center">${t("auth.newHere")} <a class="bw-link" href="#/cadastro">${t("auth.createAccount")}</a></p>
    </form>`);
  const form = root.querySelector("form")!;
  bindForm<{ login: string; password: string }>(
    form,
    async (v) => {
      setUser(await api.login({ login: v.login.trim(), password: v.password }));
      navigate(query.get("voltar") ?? "/menu");
    },
    (v) => ({
      ...(v.login.trim() ? {} : { login: t("auth.err.login") }),
      ...(v.password ? {} : { password: t("auth.err.password") }),
    }),
  );
  form.querySelector<HTMLInputElement>("input")!.focus();
};

export const registerView: View = (root) => {
  root.innerHTML = screen(t("auth.createAccount"), "/entrar", `
    <form class="bw-panel narrow bw-stack" novalidate>
      <div class="bw-panel__header">${t("auth.createAccount")}</div>
      <div data-alert hidden></div>
      ${field("username", t("auth.username"), { autocomplete: "username", hint: t("auth.usernameHint") })}
      ${field("email", t("auth.email"), { type: "email", autocomplete: "email", hint: t("auth.emailHint") })}
      ${field("nickname", t("auth.nickname"), { hint: t("auth.nicknameHint") })}
      ${field("password", t("auth.password"), { type: "password", autocomplete: "new-password", hint: t("auth.passwordHint") })}
      ${field("confirm", t("auth.confirm"), { type: "password", autocomplete: "new-password" })}
      <button class="bw-btn bw-btn--green bw-btn--block" type="submit">${t("auth.createAccount")}</button>
      <p class="bw-dim center">${t("auth.haveAccount")} <a class="bw-link" href="#/entrar">${t("auth.login")}</a></p>
    </form>`);
  const form = root.querySelector("form")!;
  const username = form.querySelector<HTMLInputElement>('[name="username"]')!;
  username.addEventListener("input", () => (username.value = username.value.toLowerCase()));
  bindForm<{ username: string; email: string; nickname: string; password: string; confirm: string }>(
    form,
    async (v) => {
      setUser(await api.register({ username: v.username.trim(), email: v.email.trim(), nickname: v.nickname.trim(), password: v.password }));
      navigate("/tutorial?novo=1");
    },
    (v) => {
      const e: Record<string, string> = {};
      if (!USERNAME_RE.test(v.username.trim())) e.username = t("auth.err.username");
      if (!/^\S+@\S+\.\S+$/.test(v.email.trim())) e.email = t("auth.err.email");
      const n = v.nickname.trim().length;
      if (n < 3 || n > 16) e.nickname = t("auth.err.nickname");
      if (v.password.length < 8) e.password = t("auth.err.passwordShort");
      if (v.confirm !== v.password) e.confirm = t("auth.err.confirm");
      return e;
    },
  );
  username.focus();
};

export const forgotView: View = (root) => {
  root.innerHTML = screen(t("auth.forgot"), "/entrar", `
    <form class="bw-panel narrow bw-stack" novalidate>
      <div class="bw-panel__header">${t("auth.forgot")}</div>
      <div class="bw-textbox">${t("auth.forgotText")}</div>
      <div data-alert hidden></div>
      ${field("email", t("auth.email"), { type: "email", autocomplete: "email" })}
      <button class="bw-btn bw-btn--blue bw-btn--block" type="submit">${t("auth.sendLink")}</button>
      <a class="bw-link center" href="#/entrar">${t("auth.backToLogin")}</a>
    </form>`);
  const form = root.querySelector("form")!;
  const { showAlert } = bindForm<{ email: string }>(
    form,
    async (v) => {
      await api.forgotPassword(v.email.trim());
      showAlert(t("auth.forgotSent"), "ok");
    },
    (v): Record<string, string> => (/^\S+@\S+\.\S+$/.test(v.email.trim()) ? {} : { email: t("auth.err.email") }),
  );
};

export const resetView: View = (root, { query }) => {
  const token = query.get("token") ?? "";
  root.innerHTML = screen(t("auth.newPassword"), "/entrar", `
    <form class="bw-panel narrow bw-stack" novalidate>
      <div class="bw-panel__header">${t("auth.newPasswordTitle")}</div>
      <div data-alert hidden></div>
      ${field("password", t("auth.newPassword"), { type: "password", autocomplete: "new-password", hint: t("auth.passwordHint") })}
      ${field("confirm", t("auth.confirm"), { type: "password", autocomplete: "new-password" })}
      <button class="bw-btn bw-btn--green bw-btn--block" type="submit">${t("auth.savePassword")}</button>
    </form>`);
  const form = root.querySelector("form")!;
  const { showAlert } = bindForm<{ password: string; confirm: string }>(
    form,
    async (v) => {
      await api.resetPassword(token, v.password);
      showAlert(t("auth.passwordChanged"), "ok");
      setTimeout(() => navigate("/entrar"), 1500);
    },
    (v) => ({
      ...(v.password.length >= 8 ? {} : { password: t("auth.err.passwordShort") }),
      ...(v.confirm === v.password ? {} : { confirm: t("auth.err.confirm") }),
    }),
  );
  if (!token) showAlert(t("auth.linkIncomplete"));
};

export const verifyEmailView: View = (root, { query }) => {
  root.innerHTML = screen(t("auth.verifyTitle"), "/menu", `
    <section class="bw-panel narrow bw-stack">
      <div class="bw-panel__header">${t("auth.verifyTitle")}</div>
      <div class="bw-textbox" data-msg>${t("auth.verifying")}</div>
      <a class="bw-btn bw-btn--blue bw-btn--block" href="#/menu">${t("auth.goMenu")}</a>
    </section>`);
  const msg = root.querySelector<HTMLElement>("[data-msg]")!;
  api
    .verifyEmail(query.get("token") ?? "")
    .then(async () => {
      msg.textContent = t("auth.verified");
      try {
        setUser(await api.me());
      } catch {
        /* não logado neste navegador: tudo bem */
      }
    })
    .catch(() => (msg.textContent = t("auth.verifyFailed")));
};
